import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api';

// Tokens are in-memory only — never localStorage — to prevent XSS theft.
// The refresh token lives in an httpOnly cookie (handled server-side).
let _accessToken: string | null = null;
let _csrfToken: string | null = null;

class ApiClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;
  private isRedirecting = false;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      timeout: 30_000,
      withCredentials: true, // sends httpOnly refresh cookie automatically
      headers: { 'Content-Type': 'application/json' },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // ── Request: attach access token + CSRF ───────────────────────────────────
    this.client.interceptors.request.use((config) => {
      if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;

      const mutating = ['POST', 'PUT', 'DELETE', 'PATCH'];
      if (mutating.includes((config.method ?? '').toUpperCase()) && _csrfToken) {
        config.headers['X-CSRF-Token'] = _csrfToken;
      }

      if (config.data instanceof FormData) delete config.headers['Content-Type'];
      return config;
    });

    // ── Response: capture CSRF, handle 401 with silent refresh ────────────────
    this.client.interceptors.response.use(
      (res) => {
        const csrf = res.headers['x-csrf-token'];
        if (csrf) _csrfToken = csrf;
        return res;
      },
      async (err) => {
        const status = err.response?.status;
        // When responseType is 'arraybuffer' (e.g. TTS calls), the error body
        // arrives as an ArrayBuffer instead of a parsed object. Decode it so the
        // CSRF and token-refresh checks below work identically for all endpoints.
        const rawData = err.response?.data;
        let parsedData: { code?: string } = {};
        if (rawData instanceof ArrayBuffer && rawData.byteLength > 0) {
          try {
            parsedData = JSON.parse(new TextDecoder().decode(rawData)) as { code?: string };
          } catch {
            // Not JSON — leave parsedData empty
          }
        } else if (rawData && typeof rawData === 'object') {
          parsedData = rawData as { code?: string };
        }
        const code = parsedData.code;
        const config = err.config;

        // CSRF mismatch — clear and let caller retry
        if (
          (status === 400 || status === 403) &&
          (code === 'CSRF_TOKEN_MISSING' || code === 'CSRF_TOKEN_INVALID')
        ) {
          _csrfToken = null;
          err.isCSRFError = true;
          return Promise.reject(err);
        }

        // Access token expired — attempt silent refresh once
        if (status === 401 && code === 'TOKEN_EXPIRED' && !config._retried) {
          config._retried = true;
          try {
            const newAccess = await this.refreshAccessToken();
            config.headers.Authorization = `Bearer ${newAccess}`;
            return this.client(config);
          } catch {
            // Refresh failed — force logout only if the user was authenticated
            this.handleSessionExpired();
            return Promise.reject(err);
          }
        }

        // Any other 401 that isn't the refresh endpoint itself.
        // Only force logout when the request actually included an Authorization
        // header — if it didn't, the 401 is expected (user isn't authenticated
        // yet, e.g. during initial silent-refresh on page load) and should NOT
        // redirect. Without this guard, requests made before the refresh
        // completes trigger a spurious logout loop.
        if (
          status === 401 &&
          !config._retried &&
          !config.url?.includes('/auth/refresh') &&
          config.headers?.Authorization
        ) {
          this.handleSessionExpired();
        }

        return Promise.reject(err);
      },
    );
  }

  /** Deduplicated silent token refresh — only one in-flight request at a time. */
  private refreshAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.client
        .post('/auth/refresh')
        .then((res) => {
          const token = res.data?.data?.accessToken;
          if (!token) throw new Error('No access token in refresh response');
          _accessToken = token;
          return token;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  private handleSessionExpired() {
    if (this.isRedirecting) return;
    this.isRedirecting = true;
    _accessToken = null;
    _csrfToken = null;
    setTimeout(() => {
      this.isRedirecting = false;
    }, 3_000);
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // ── Public API ───────────────────────────────────────────────────────────────

  setAccessToken(token: string) {
    _accessToken = token;
  }
  getAccessToken() {
    return _accessToken;
  }
  clearAccessToken() {
    _accessToken = null;
    _csrfToken = null;
  }
  hasAccessToken() {
    return !!_accessToken;
  }
  setCSRFToken(token: string) {
    _csrfToken = token;
  }
  getCSRFToken() {
    return _csrfToken;
  }
  clearCSRFToken() {
    _csrfToken = null;
  }

  async fetchCSRFToken(): Promise<void> {
    const res = await this.client.get('/me');
    const csrf = res.headers['x-csrf-token'];
    if (csrf) _csrfToken = csrf;
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.get<T>(url, config);
    return res.data;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.post<T>(url, data, config);
    return res.data;
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.patch<T>(url, data, config);
    return res.data;
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.put<T>(url, data, config);
    return res.data;
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.delete<T>(url, config);
    return res.data;
  }

  async upload<T = unknown>(
    url: string,
    formData: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<T> {
    const res = await this.client.post<T>(url, formData, {
      timeout: 60_000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
